/**
 * Admin UI language (docs/features/org-language.md §6).
 *
 * Ukrainian is the source text in every template and component; English is loaded at startup
 * as Angular runtime translations (`$localize` / `i18n` markers) from assets/i18n/en.json.
 * The language is decided BEFORE Angular boots (main.ts), so it lives in localStorage:
 * the club's language (OrganizationConfigDto.language) is the default, a UA/EN switch in the
 * user menu overrides it for this browser. A change needs one reload.
 */
export type UiLanguage = 'uk' | 'en';

export const LANGUAGE_KEY = 'clubiq.language';
/** Set once the user picks a language by hand; the org default no longer overrides it. */
export const LANGUAGE_OVERRIDE_KEY = 'clubiq.language.override';

export function storedLanguage(): UiLanguage {
  try {
    return localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'uk';
  } catch {
    return 'uk';
  }
}

export function hasLanguageOverride(): boolean {
  try {
    return localStorage.getItem(LANGUAGE_OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}

/** The user chose explicitly (header switch): store, mark the override, reload if it changed. */
export function chooseLanguage(lang: UiLanguage): void {
  const current = storedLanguage();
  try {
    localStorage.setItem(LANGUAGE_KEY, lang);
    localStorage.setItem(LANGUAGE_OVERRIDE_KEY, '1');
  } catch {
    // private mode — the choice lasts until the next reload only
  }
  if (current !== lang) {
    location.reload();
  }
}

/**
 * The club's language arrived with /organization/config: adopt it unless the user overrode it
 * in this browser; reload once when it differs from what Angular booted with.
 */
export function applyOrganizationLanguage(orgLanguage: string | null | undefined): void {
  if (hasLanguageOverride()) return;
  const wanted: UiLanguage = orgLanguage === 'en' ? 'en' : 'uk';
  if (wanted === storedLanguage()) return;
  try {
    localStorage.setItem(LANGUAGE_KEY, wanted);
  } catch {
    return;
  }
  location.reload();
}

/**
 * Runtime dictionary for Ukrainian literals that live inside template EXPRESSIONS
 * (`{{ cond ? 'А' : 'Б' }}`, `[title]="'…'"`), where `$localize` cannot reach: the `t` pipe
 * looks the source text up here. Filled by main.ts from en.json#expressions; empty for uk.
 */
export const expressionTranslations: Record<string, string> = {};

export function tx(source: string | null | undefined): string {
  if (source == null) return '';
  const hit = expressionTranslations[source];
  return hit && hit.length ? hit : source;
}

/**
 * «N спортсменів» agreement for a count word. Pass the three Ukrainian forms already wrapped in
 * `$localize` (one / few / many); Ukrainian picks by its own rule (1, 21, 31 … → one; 2–4, 22–24 …
 * → few; the rest → many), English has only singular and plural, so anything but exactly 1 takes
 * the «many» translation — en.json maps both «few» and «many» sources to the same English plural.
 */
export function pluralForm(count: number, one: string, few: string, many: string): string {
  if (storedLanguage() === 'en') return count === 1 ? one : many;
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
